import { FiFileText } from 'react-icons/fi'
import { looksLikeImageUrl } from './CardArtifactsGrid'
import CardDocumentList from './CardDocumentList'

/** API may return a signed URL string or { url, gsUrl, contentHash }. */
function resolveMediaUrl(item) {
  if (item == null) return null
  if (typeof item === 'string') {
    const trimmed = item.trim()
    return trimmed || null
  }
  if (typeof item === 'object') {
    const raw = item.url ?? item.gsUrl ?? item.src
    if (typeof raw === 'string') {
      const trimmed = raw.trim()
      return trimmed || null
    }
  }
  return null
}

function normalizeMediaList(arr) {
  if (!Array.isArray(arr)) return []
  return arr.map(resolveMediaUrl).filter(Boolean)
}

function mediaKind(url) {
  if (!url || typeof url !== 'string') return 'other'
  const path = url.split('?')[0].toLowerCase()
  if (/\.(mp4|webm|mov|m4v|ogv)(\?|$)/i.test(path)) return 'video'
  if (/\.(jpg|jpeg|png|gif|webp|avif|bmp)(\?|$)/i.test(path)) return 'image'
  if (/\.pdf(\?|$)/i.test(path)) return 'pdf'
  return 'other'
}

function buildOwnerItems({
  frontImageUrl,
  backImageUrl,
  quoteVideoUrls,
  quoteDocumentUrls,
  ownerStoryMediaUrl,
  ownerDeclarationMediaUrl,
}) {
  const items = []
  const push = (item) =>
    items.push({ ...item, source: 'owner', key: item.key ?? `owner-${items.length}` })

  if (frontImageUrl) {
    push({
      type: 'image',
      key: 'card-front',
      label: 'Card front',
      src: frontImageUrl,
      alt: 'Card front',
    })
  }
  if (backImageUrl) {
    push({
      type: 'image',
      key: 'card-back',
      label: 'Card back',
      src: backImageUrl,
      alt: 'Card back',
    })
  }

  const videos = normalizeMediaList(quoteVideoUrls)
  videos.forEach((url, i) => {
    push({
      type: 'video',
      key: `quote-v-${i}`,
      label: videos.length > 1 ? `Submission video ${i + 1}` : 'Submission video',
      src: url,
    })
  })

  const story = ownerStoryMediaUrl || null
  const decl = ownerDeclarationMediaUrl || null
  const storyKind = story ? mediaKind(story) : null
  const declKind = decl ? mediaKind(decl) : null

  if (story) {
    push({
      type: 'story',
      key: 'owner-story',
      label: 'Story media',
      storySrc: story,
      mediaKind: storyKind,
      href: storyKind !== 'image' && storyKind !== 'video' ? story : undefined,
    })
  }

  if (decl) {
    if (declKind === 'image') {
      push({
        type: 'image',
        key: 'declaration',
        label: 'Declaration',
        src: decl,
        alt: 'Declaration',
        href: decl,
      })
    } else if (declKind === 'video') {
      push({ type: 'video', key: 'declaration-v', label: 'Declaration', src: decl })
    } else {
      push({
        type: 'file',
        key: 'declaration-f',
        label: 'Declaration',
        href: decl,
        icon: FiFileText,
      })
    }
  }

  const docs = normalizeMediaList(quoteDocumentUrls)
  docs.forEach((url, i) => {
    push({
      type: 'file',
      key: `quote-doc-${i}`,
      label: docs.length > 1 ? `Document ${i + 1}` : 'Document',
      href: url,
      icon: FiFileText,
    })
  })

  return items
}

function normalizeLinkList(arr) {
  if (!Array.isArray(arr)) return []
  const seen = new Set()
  const out = []
  for (const item of arr) {
    const u =
      typeof item === 'string'
        ? item.trim()
        : item && typeof item === 'object'
          ? String(item.url || item.href || '').trim()
          : ''
    if (!/^https?:\/\//i.test(u) || seen.has(u)) continue
    seen.add(u)
    out.push(u)
  }
  return out
}

function buildGraderItems({
  graderInspectionPhotoUrls,
  graderInspectionVideoUrls,
  graderInspectionDocumentUrls,
  graderReferenceLinks,
  photoRollAnchorId,
}) {
  const items = []
  const push = (item) =>
    items.push({ ...item, source: 'grader', key: item.key ?? `grader-${items.length}` })

  const gPhotos = normalizeMediaList(graderInspectionPhotoUrls)
  const gVideos = normalizeMediaList(graderInspectionVideoUrls)
  const gDocs = normalizeMediaList(graderInspectionDocumentUrls)

  gVideos.forEach((url, i) => {
    push({
      type: 'video',
      key: `grader-v-${i}`,
      label: gVideos.length > 1 ? `Inspection video ${i + 1}` : 'Inspection video',
      src: url,
    })
  })

  gDocs.forEach((url, i) => {
    push({
      type: 'file',
      key: `grader-doc-${i}`,
      label: gDocs.length > 1 ? `Inspection doc ${i + 1}` : 'Inspection document',
      href: url,
      icon: FiFileText,
    })
  })

  const gLinks = normalizeLinkList(graderReferenceLinks)
  gLinks.forEach((url, i) => {
    push({
      type: 'link',
      key: `grader-link-${i}`,
      label: gLinks.length > 1 ? `Reference link ${i + 1}` : 'Reference link',
      href: url,
    })
  })

  if (gPhotos.length > 0) {
    push({
      type: 'gallery',
      key: 'grader-photo-gallery',
      label: 'Inspection photos',
      subtitle: `${gPhotos.length} photos`,
      previewSrc: gPhotos[0],
      count: gPhotos.length,
      onActivate: () => {
        const el = document.getElementById(photoRollAnchorId)
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      },
    })
  }

  return { items, gPhotos }
}

function buildLabItems({
  labPhotoUrls,
  labVideoUrls,
  labCertificateUrls,
  labDocumentUrls,
  labNewsLinks,
  photoRollAnchorId,
}) {
  const items = []
  const push = (item) =>
    items.push({ ...item, source: 'lab', key: item.key ?? `lab-${items.length}` })

  const lPhotos = normalizeMediaList(labPhotoUrls)
  const lVideos = normalizeMediaList(labVideoUrls)
  const lCerts = normalizeMediaList(labCertificateUrls)
  const lDocs = normalizeMediaList(labDocumentUrls)
  const lLinks = normalizeLinkList(labNewsLinks)

  lVideos.forEach((url, i) => {
    push({
      type: 'video',
      key: `lab-v-${i}`,
      label: lVideos.length > 1 ? `Lab video ${i + 1}` : 'Lab video',
      src: url,
    })
  })

  lCerts.forEach((url, i) => {
    const preview = looksLikeImageUrl(url) ? url : undefined
    push({
      type: 'certificate',
      key: `lab-cert-${i}`,
      label: lCerts.length > 1 ? `Certificate ${i + 1}` : 'Certificate',
      href: url,
      previewSrc: preview,
    })
  })

  lDocs.forEach((url, i) => {
    push({
      type: 'file',
      key: `lab-doc-${i}`,
      label: lDocs.length > 1 ? `Lab document ${i + 1}` : 'Lab document',
      href: url,
      icon: FiFileText,
    })
  })

  lLinks.forEach((url, i) => {
    push({
      type: 'link',
      key: `lab-link-${i}`,
      label: lLinks.length > 1 ? `News link ${i + 1}` : 'News link',
      href: url,
    })
  })

  if (lPhotos.length > 0) {
    push({
      type: 'gallery',
      key: 'lab-photo-gallery',
      label: 'Professional photos',
      subtitle: `${lPhotos.length} photos`,
      previewSrc: lPhotos[0],
      count: lPhotos.length,
      onActivate: () => {
        const el = document.getElementById(photoRollAnchorId)
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      },
    })
  }

  return { items, lPhotos }
}

const VIEWER_ITEM_TYPES = new Set(['image', 'video', 'gallery'])

function isViewerItem(item) {
  if (VIEWER_ITEM_TYPES.has(item.type)) return true
  return item.type === 'story' && (item.mediaKind === 'image' || item.mediaKind === 'video')
}

/**
 * Photos and videos from every stage, flattened for the immersive viewers
 * (photo shoot row and video reel). Everything else stays in the artifact grid.
 */
export function collectCardMedia({
  frontImageUrl,
  backImageUrl,
  quoteVideoUrls,
  ownerStoryMediaUrl,
  ownerDeclarationMediaUrl,
  graderInspectionPhotoUrls,
  graderInspectionVideoUrls,
  labPhotoUrls,
  labVideoUrls,
}) {
  const images = []
  const videos = []
  const seen = new Set()
  const addImage = (url, label) => {
    if (!url || seen.has(url)) return
    seen.add(url)
    images.push({ url, label })
  }
  const addVideo = (url, label) => {
    if (!url || seen.has(url)) return
    seen.add(url)
    videos.push({ url, label })
  }
  const addList = (list, label, add) => {
    const urls = normalizeMediaList(list)
    urls.forEach((url, i) => add(url, urls.length > 1 ? `${label} ${i + 1}` : label))
  }

  addImage(frontImageUrl, 'Front')
  addImage(backImageUrl, 'Back')
  addList(graderInspectionPhotoUrls, 'Inspection', addImage)
  addList(labPhotoUrls, 'Lab', addImage)
  addList(quoteVideoUrls, 'Submission', addVideo)
  addList(graderInspectionVideoUrls, 'Inspection', addVideo)
  addList(labVideoUrls, 'Lab', addVideo)
  ;[
    [ownerStoryMediaUrl, 'Story'],
    [ownerDeclarationMediaUrl, 'Declaration'],
  ].forEach(([url, label]) => {
    const kind = mediaKind(url)
    if (kind === 'image') addImage(url, label)
    if (kind === 'video') addVideo(url, label)
  })

  return { images, videos }
}

/**
 * Owner card detail — documents and links grouped by source (Owner / Grader / M1NT Lab).
 * Photos and videos live in the shoot row and video reel instead.
 */
export default function CardDetailMediaGallery({
  frontImageUrl,
  backImageUrl,
  quoteVideoUrls = [],
  quoteDocumentUrls = [],
  ownerStoryMediaUrl,
  ownerDeclarationMediaUrl,
  graderInspectionPhotoUrls = [],
  graderInspectionVideoUrls = [],
  graderInspectionDocumentUrls = [],
  graderReferenceLinks = [],
  labPhotoUrls = [],
  labVideoUrls = [],
  labCertificateUrls = [],
  labDocumentUrls = [],
  labNewsLinks = [],
}) {
  const photoRollAnchorId = 'artifact-photo-roll'

  const ownerItemsAll = buildOwnerItems({
    frontImageUrl,
    backImageUrl,
    quoteVideoUrls,
    quoteDocumentUrls,
    ownerStoryMediaUrl,
    ownerDeclarationMediaUrl,
  })

  const { items: graderItemsAll } = buildGraderItems({
    graderInspectionPhotoUrls,
    graderInspectionVideoUrls,
    graderInspectionDocumentUrls,
    graderReferenceLinks,
    photoRollAnchorId,
  })

  const { items: labItemsAll } = buildLabItems({
    labPhotoUrls,
    labVideoUrls,
    labCertificateUrls,
    labDocumentUrls,
    labNewsLinks,
    photoRollAnchorId,
  })

  const ownerItems = ownerItemsAll.filter((item) => !isViewerItem(item))
  const graderItems = graderItemsAll.filter((item) => !isViewerItem(item))
  const labItems = labItemsAll.filter((item) => !isViewerItem(item))
  const hasAny = ownerItems.length > 0 || graderItems.length > 0 || labItems.length > 0

  if (!hasAny) return null

  return (
    <CardDocumentList
      groups={[
        { source: 'owner', items: ownerItems },
        { source: 'grader', items: graderItems },
        { source: 'lab', items: labItems },
      ]}
    />
  )
}
